import React, { useMemo } from 'react';
import { Box } from '@mui/material';

const IS_DEV = process.env.NODE_ENV === 'development';

function formatChars(n) {
  const v = Math.max(0, Math.round(Number(n) || 0));
  if (v < 1000) return `${v} chars`;
  if (v < 10000) return `${(v / 1000).toFixed(1)}k chars`;
  return `${Math.round(v / 1000)}k chars`;
}

function balancedJsonEnd(text, start) {
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < text.length; i += 1) {
    const ch = text[i];
    if (inString) {
      if (escape) escape = false;
      else if (ch === '\\') escape = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{' || ch === '[') depth += 1;
    else if (ch === '}' || ch === ']') {
      depth -= 1;
      if (depth === 0) return i + 1;
    }
  }
  return -1;
}

/** Indent large JSON blobs so the prompt is scannable. The words stay the same. */
function prettyEmbeddedJson(text) {
  const src = String(text || '');
  let out = '';
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    const boundary = i === 0 || /[\s:=]/.test(src[i - 1]);
    if (boundary && (ch === '{' || ch === '[')) {
      const end = balancedJsonEnd(src, i);
      if (end > i) {
        const slice = src.slice(i, end);
        if (slice.length >= 160) {
          try {
            out += JSON.stringify(JSON.parse(slice), null, 2);
            i = end;
            continue;
          } catch {
            // Leave the original characters in place.
          }
        }
      }
    }
    out += ch;
    i += 1;
  }
  return out;
}

function metaLine(input) {
  const bits = [];
  if (input.model) bits.push(input.model);
  if (input.temperature != null) bits.push(`temp ${input.temperature}`);
  if (input.thinkingLevel) bits.push(`thinking ${input.thinkingLevel}`);
  else if (input.thinkingBudget != null && input.thinkingBudget !== '') {
    bits.push(`thinking budget ${input.thinkingBudget}`);
  }
  if (Array.isArray(input.tools) && input.tools.length) bits.push(input.tools.join(' + '));
  if (input.imageAttached) bits.push('viewport image attached');
  return bits.join(' · ');
}

/**
 * Dev-only: the prompt and context sent to the model.
 * Closed until clicked. Each part opens on its own.
 */
const BoardAiInputDebug = ({ input = null, className = '' }) => {
  const sections = useMemo(
    () => (Array.isArray(input?.sections) ? input.sections.filter(section => section?.text) : []),
    [input]
  );
  const pretty = useMemo(
    () => sections.map(section => prettyEmbeddedJson(section.text)),
    [sections]
  );

  if (!IS_DEV || !sections.length) return null;

  const total = sections.reduce((sum, section) => sum + String(section.text || '').length, 0);
  const meta = metaLine(input);

  return (
    <Box
      className={`board-ai-input-debug ${className}`.trim()}
      component="details"
    >
      <Box component="summary" className="board-ai-input-debug-summary">
        Sent to AI
        {' · '}
        {sections.length === 1 ? '1 part' : `${sections.length} parts`}
        {' · '}
        {formatChars(total)}
      </Box>
      <Box className="board-ai-input-debug-body">
        {meta ? (
          <Box component="p" className="board-ai-input-debug-meta">{meta}</Box>
        ) : null}
        <Box component="p" className="board-ai-input-debug-note">
          JSON blocks are indented so they are easier to read. This is the text the model received.
        </Box>
        {sections.map((section, idx) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={`${section.label || 'part'}-${idx}`}
            component="details"
            className="board-ai-input-debug-section"
          >
            <Box component="summary" className="board-ai-input-debug-section-summary">
              {section.label || 'Part'}
              {' · '}
              {formatChars(String(section.text || '').length)}
            </Box>
            <Box component="pre" className="board-ai-input-debug-pre">
              {pretty[idx]}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default BoardAiInputDebug;
